import bus from '@/utils/bus';
import enums from '@/utils/enums';
import requestService from '@/services/request';
import emailCheck from '@/utils/emailCheck';
import { APP, USER, UNIT, CONFIG, ROLES } from '@/utils/constants';
import { hasPermission, hasPermittedRole } from '@/utils';
import logTrace from '@/services/logTrace';

export default {
  computed: {
    ROLES() {
      return ROLES;
    },
    touchable() {
      return 'ontouchstart' in document.documentElement;
    },

    mobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },

    user() {
      return this.$store.getters[USER.GET_USER];
    },
    isClient() {
      return this.user && this.user.userRole === ROLES.CLIENT_ROLE;
    },
    isSyndic() {
      return this.user && this.user.userRole === ROLES.SYNDIC_ROLE;
    },

    userFirstName() {
      return this.user
        ? this.user.fullName
            .substring(0, this.user.fullName.indexOf(' '))
            .toLowerCase()
        : '';
    },

    units() {
      return this.$store.getters[UNIT.GET_ALL];
    },

    selectedUnit() {
      return this.$store.getters[UNIT.GET_SELECTED];
    },

    loadingApp() {
      return this.$store.getters[APP.ISLOADING];
    },

    enums() {
      return enums;
    },

    isExceptionProduct() {
      const product = this.$store.getters[UNIT.GET_SELECTED_PRODUCT];
      if (product) {
        const unitObj = this.$store.getters[UNIT.GET_SELECTED];
        const isException = !!product.blocks.find(
          (block) => block.id === unitObj.block.id && block.type === 'Comercial'
        );
        return isException;
      }
      return false;
    },

    getByEnterpriseParams() {
      return this.selectedUnit
        ? {
            product: this.selectedUnit.product.id,
            block: this.selectedUnit.block.id,
            unit: this.selectedUnit.unit.code,
            syndicEnabled: this.isSyndic ? true : undefined,
          }
        : null;
    },

    getByEnterpriseFromDateParams() {
      return this.selectedUnit
        ? {
            ...this.getByEnterpriseParams,
            fromDate: this.selectedUnit.contracts[0].contractedAt
              ? this.selectedUnit.contracts[0].contractedAt
              : this.selectedUnit.contracts[0].contractedDataBase,
          }
        : null;
    },
  },

  data() {
    return {
      rule: {
        required: [(v) => !!v || 'Campo de preenchimento obrigatório'],
        email: [(v) => !v || emailCheck(v) || 'E-mail inválido'],
        requiredEmail: [
          (v) => !!v || 'Campo de preenchimento obrigatório',
          (v) => emailCheck(v) || 'E-mail inválido',
        ],
        numberRequired: [
          (v) =>
            !!parseFloat(v.replace(/[^0-9\.]+/g, '')) ||
            'Campo de preenchimento obrigatório',
        ],
        code: [(v) => v.length === 6 || 'Senha temporária inválida'],
        verificationCode: [(v) => v.length === 6 || 'Código inválido'],
        oldPassword: [
          (v) =>
            (v.length >= 8 && v.length <= 15) ||
            'A senha deve ter no mínimo de 8 e máximo de 15 caracteres.',
        ],
        password: [
          (v) =>
            (v.length >= 8 && v.length <= 15) ||
            'A senha deve ter no mínimo de 8 e máximo de 15 caracteres.',
          (v) =>
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[?!@#$%&*])[A-Za-z\d?!@#$%&*]{8,15}$/.test(
              v
            ) ||
            'A senha deve atender o seguinte padrão:<br/>   → Pelo menos um caracter minúsculo.<br/>   → Pelo menos um caracter maiúsculo.<br/>   → Pelo menos um número.<br/>   → Pelo menos um caracter especial.  <b>?!@#$%&*</b><br/>',
        ],
      },
    };
  },

  methods: {
    ...require('../utils/stringUtils'),

    getContractTitle({ type, detachedType }) {
      const { contractTypes, detachedContractTypes } =
        this.$store.getters[CONFIG.GET].navigationRules.financial;

      if (type === enums.contractValues.detached) {
        return [
          ...new Set(detachedType.map((o) => detachedContractTypes[o])),
        ].join(', ');
      }

      return contractTypes[type];
    },

    getAllowedContracts(module, contracts) {
      const { notAllowedContracts } =
        this.$store.getters[CONFIG.GET].navigationRules.financial;
      const notAlloweds = notAllowedContracts[module];

      return contracts.filter(
        (contract) => !notAlloweds.includes(contract.type)
      );
    },

    createTrace(action, details = {}, contractId = null) {
      logTrace({
        action,
        details,
        contractId,
      });
    },

    goBack() {
      this.$router.go(-1);
    },

    hasPermissionToView(item) {
      let _hasPermission = true;
      let _hasRole = true;
      if (item.vuexLink && !this.$store.getters[item.vuexLink]) {
        return false;
      }
      if (item && item.meta) {
        if (item.meta.profiles) {
          _hasPermission = hasPermission(item.meta.profiles);
        }
        if (item.meta.roles) {
          _hasRole = hasPermittedRole(item.meta.roles);
        }
      }
      return _hasPermission && _hasRole;
    },

    handleAuthError(err) {
      const message = err.data.message;
      const invalidDocument = {
        username: this.$store.getters[CONFIG.GET].texts.invalidDocument,
        document: this.$store.getters[CONFIG.GET].texts.invalidDocument,
      };

      let snackbarMessage;
      if (message === 'Password is wrong')
        return this.enums.responses.invalidPassword;
      if (message === 'Password does not match')
        return this.enums.responses.oldPasswordNotMatch;
      if (message === 'userDoesNotExistForResetPassword')
        return this.enums.responses.userDoesNotExistForResetPassword;
      if (message === 'Reset code is wrong')
        return this.enums.responses.invalidCode;
      if (message === 'This user does not exist')
        return this.enums.responses.invalidLogin;
      if (message === 'This user is not enabled or does not have authorization')
        return this.enums.responses.userDoesNotExistForResetPassword;
      if (message === 'This user already exists')
        return this.enums.responses.userExists;
      if (message === 'This user dont have reset code')
        return this.enums.responses.userAlreadyChangedItsPassword;
      if (message === 'This user does not exist on Imobme')
        return invalidDocument;
      if (message === 'This user does not have a valid contract')
        return invalidDocument;
      if (message === 'This user has not been verified')
        return this.enums.responses.invalidLogin;
      if (message === 'The reset code has expirated')
        return this.enums.responses.codeExpire;
      if (message === 'Another user has this email')
        snackbarMessage = this.enums.responses.emailRepeated;

      if (message === 'User is blocked, try again one hour later')
        snackbarMessage = this.enums.responses.userBlocked;
      if (message === 'Incorrect credentials')
        snackbarMessage = this.enums.responses.invalidLogin;

      bus.$emit('snackbar:open', {
        text: snackbarMessage ? snackbarMessage : this.enums.responses.error,
        color: 'red',
      });
    },

    //! deprecated
    request(
      url,
      method = 'GET',
      data = null,
      params = null,
      localErrorHandling = false,
      auth = true,
      customHeaders = null,
      useLoading = true,
      sendTenant = true
    ) {
      const options = {
        data,
        params,
        localErrorHandling,
        auth,
        customHeaders,
        useLoading,
        sendTenant,
      };
      return requestService(url, method, options);
    },

    requestService(
      url,
      method = 'GET',
      options = ({
        data = null,
        params = null,
        localErrorHandling = false,
        auth = true,
        customHeaders = null,
        useLoading = true,
        sendTenant = true,
        retries = null,
        overwriteOptions = {},
      } = {})
    ) {
      try {
        return requestService(url, method, options);
      } catch (err) {
        throw err;
      }
    },
  },
};



// WEBPACK FOOTER //
// ./src/mixins/global.js